import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../../redux/helpers/axios";
import inventory from "../../redux/helpers/inventory";

/**
 * @author
 * @function AddProduct
 *
 * Admin page for creating an ElevenProduct (with gallery + variations).
 **/

const ENDPOINTS = {
  categories: "/eleven/categories",
  createProduct: "/eleven/products/create",
};

const STORES = [
  "A1688",
  "ALIBABA",
  "TMALL",
  "TAOBAO",
  "ALIEXPRESS",
  "AMAZON",
  "EBAY",
  "ETSY",
  "WALMART",
  "OTHER",
];

const slugify = (text = "") =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

const emptyVariation = () => ({
  key: Math.random().toString(36).slice(2),
  sku: "",
  variationName: "",
  variationImg: "",
  price: "",
});

const initialForm = {
  pid: "",
  productTitle: "",
  slug: "",
  elevenCategoryId: "",
  productUrl: "",
  productImg: "",
  price: "",
  store: "A1688",
  descHtml: "",
  isFeatured: false,
};

/* ---------- image URL helpers ---------- */

function getAliCdnUrl(url) {
  const filename = url.split("/").pop().split("?")[0];
  return `https://img.alicdn.com/${filename}`;
}

// Only rewrite Alibaba CDN URLs (cbu01.alicdn.com, etc.); leave others alone.
const toCdn = (url) => {
  const u = String(url || "").trim();
  if (!u) return "";
  const abs = u.startsWith("//") ? `https:${u}` : u;
  return /alicdn\.com/i.test(abs) ? getAliCdnUrl(abs) : abs;
};

// Rewrite every <img> in description HTML and add no-referrer.
const normalizeDescHtml = (html = "") => {
  if (!html || typeof window === "undefined") return html || "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("img").forEach((img) => {
    const src =
      img.getAttribute("src") ||
      img.getAttribute("data-src") ||
      img.getAttribute("data-lazyload-src") ||
      "";
    if (src) img.setAttribute("src", toCdn(src));
    img.removeAttribute("data-src");
    img.removeAttribute("data-lazyload-src");
    img.setAttribute("referrerpolicy", "no-referrer");
  });
  return doc.body.innerHTML;
};

/* ---------- inventory product -> form mapping ---------- */

// "0:3;1:0" -> first non-empty sku_props image for those pid:vid pairs
const findSkuImage = (sku, skuProps = []) => {
  const pairs = String(sku.props_ids || "")
    .split(";")
    .map((p) => p.split(":"))
    .filter((p) => p.length === 2);
  for (const [pid, vid] of pairs) {
    const prop = skuProps.find((sp) => String(sp.pid) === pid);
    const val = prop?.values?.find((v) => String(v.vid) === vid);
    if (val?.imageUrl) return val.imageUrl;
  }
  return "";
};

// "Color:Curry;Specifications:20*10cm" -> "Curry / 20*10cm"
const skuName = (sku) =>
  String(sku.props_names || "")
    .split(";")
    .map((p) => p.split(":").slice(1).join(":").trim())
    .filter(Boolean)
    .join(" / ") || String(sku.skuid);

const mapInventoryProduct = (product) => {
  const mainImgs = [
    ...new Set((product.main_imgs || []).filter(Boolean).map(toCdn)),
  ];
  const fallbackImg = mainImgs[0] || "";
  const variations = (product.skus || []).map((sku) => ({
    ...emptyVariation(),
    sku: String(sku.skuid),
    variationName: skuName(sku),
    variationImg: toCdn(findSkuImage(sku, product.sku_props)) || fallbackImg,
    price: "",
  }));
  return {
    pid: String(product.item_id ?? ""),
    productTitle: product.title || "",
    slug: slugify(product.title || ""),
    productUrl: product.product_url || "",
    productImg: fallbackImg,
    gallery: mainImgs,
    variations,
  };
};

/* ---------- small UI helpers ---------- */

const Card = ({ title, subtitle, action, children }) => (
  <div className="bg-white rounded-lg border border-gray-200">
    {(title || action) && (
      <div className="flex items-start justify-between gap-4 px-4 py-3 border-b border-gray-100">
        <div>
          <h3 className="text-base font-semibold text-gray-800">{title}</h3>
          {subtitle && (
            <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
          )}
        </div>
        {action}
      </div>
    )}
    <div className="p-4">{children}</div>
  </div>
);

const Label = ({ children, required, htmlFor }) => (
  <label
    htmlFor={htmlFor}
    className="block text-sm font-medium text-gray-700 mb-1"
  >
    {children}
    {required && <span className="text-red-500 ml-0.5">*</span>}
  </label>
);

const inputCls = (hasError) =>
  `w-full rounded-lg border px-3 py-2 text-sm text-gray-800 placeholder-gray-400 bg-white
   focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 transition
   ${hasError ? "border-red-400" : "border-gray-300"}`;

const FieldError = ({ msg }) =>
  msg ? <p className="text-xs text-red-500 mt-1">{msg}</p> : null;

const ImgThumb = ({ src, className = "" }) => {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [src]);
  if (!src || broken)
    return (
      <div
        className={`flex items-center justify-center bg-gray-100 text-gray-400 text-[10px] rounded-lg border border-dashed border-gray-300 ${className}`}
      >
        No image
      </div>
    );
  return (
    <img
      src={toCdn(src)}
      alt=""
      referrerPolicy="no-referrer"
      onError={() => setBroken(true)}
      className={`object-cover rounded-lg border border-gray-200 ${className}`}
    />
  );
};

const FetchProductModal = ({ open, onClose, onFetched }) => {
  const [productId, setProductId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setError("");
      setLoading(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && !loading && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, loading, onClose]);

  if (!open) return null;

  const handleFetch = async (e) => {
    e?.preventDefault();
    const id = productId.trim();
    if (!id) return setError("Enter a product ID");
    setLoading(true);
    setError("");
    try {
      const [productRes, descRes] = await Promise.allSettled([
        inventory.get(`/product/find/${id}`),
        inventory.get(`/product/${id}/description`),
      ]);
      if (
        productRes.status === "rejected" ||
        !productRes.value?.data?.product
      ) {
        const msg =
          productRes.reason?.response?.data?.msg ||
          productRes.value?.data?.error ||
          "Product not found";
        throw new Error(msg);
      }
      const product = productRes.value.data.product;
      const description =
        descRes.status === "fulfilled"
          ? descRes.value?.data?.description?.detail_html || ""
          : "";
      onFetched(product, description, descRes.status === "fulfilled");
      setProductId("");
      onClose();
    } catch (err) {
      setError(err.message || "Failed to fetch product");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={() => !loading && onClose()}
      />
      <form
        onSubmit={handleFetch}
        className="relative w-full max-w-md bg-white rounded-xl shadow-xl border border-gray-200"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-base font-semibold text-gray-800">
              Fetch Product
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Auto-fill title, images, variations and description
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="w-8 h-8 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-50"
          >
            ✕
          </button>
        </div>
        <div className="p-5">
          <Label htmlFor="fetchProductId" required>
            Product ID
          </Label>
          <input
            id="fetchProductId"
            autoFocus
            value={productId}
            onChange={(e) => {
              setProductId(e.target.value);
              setError("");
            }}
            placeholder="e.g. 660113858728"
            className={inputCls(error)}
          />
          <FieldError msg={error} />
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100 bg-gray-50 rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-5 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60 inline-flex items-center gap-2"
          >
            {loading && (
              <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            )}
            {loading ? "Fetching..." : "Fetch"}
          </button>
        </div>
      </form>
    </div>
  );
};

/* ---------- page ---------- */

export const AddProduct = (props) => {
  const [form, setForm] = useState(initialForm);
  const [slugTouched, setSlugTouched] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [galleryInput, setGalleryInput] = useState("");
  const [variations, setVariations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingCats, setLoadingCats] = useState(true);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState(null); // { type: 'success' | 'error', msg }
  const [descTab, setDescTab] = useState("edit");
  const [fetchOpen, setFetchOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    const fetchCategories = async () => {
      try {
        const { data } = await axiosInstance.get(ENDPOINTS.categories);
        if (alive) setCategories(data.categories || []);
      } catch (err) {
        if (alive)
          setAlert({ type: "error", msg: "Failed to load categories" });
      } finally {
        if (alive) setLoadingCats(false);
      }
    };
    fetchCategories();
    return () => {
      alive = false;
    };
  }, []);

  const setField = (name, value) => {
    setForm((f) => {
      const next = { ...f, [name]: value };
      if (name === "productTitle" && !slugTouched) next.slug = slugify(value);
      return next;
    });
    setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setField(name, type === "checkbox" ? checked : value);
  };

  /* base price -> variation prices
   * Variations follow the base price until you edit their price by hand
   * (priceTouched). "Apply to all" overrides every variation again. */
  const clearVariationPriceErrors = () =>
    setErrors((e) => {
      const next = { ...e };
      Object.keys(next).forEach((k) => {
        if (k.startsWith("var_") && k.endsWith("_price")) delete next[k];
      });
      return next;
    });

  const onBasePriceChange = (e) => {
    const value = e.target.value;
    setField("price", value);
    setVariations((vs) =>
      vs.map((v) => (v.priceTouched ? v : { ...v, price: value })),
    );
    clearVariationPriceErrors();
  };

  const applyBasePriceToAll = () => {
    setVariations((vs) =>
      vs.map((v) => ({ ...v, price: form.price, priceTouched: false })),
    );
    clearVariationPriceErrors();
  };

  /* fetch from inventory */
  const applyFetchedProduct = (product, description, descOk) => {
    const mapped = mapInventoryProduct(product);
    setForm((f) => ({
      ...f,
      pid: mapped.pid,
      productTitle: mapped.productTitle,
      slug: mapped.slug,
      productUrl: mapped.productUrl || f.productUrl,
      productImg: mapped.productImg,
      store: mapped.productUrl.includes("1688.com") ? "A1688" : f.store,
      descHtml: description ? normalizeDescHtml(description) : f.descHtml,
    }));
    setSlugTouched(false);
    setGallery(mapped.gallery);
    setVariations(
      mapped.variations.map((v) => ({ ...v, price: form.price || "" })),
    );
    setErrors({});
    setAlert({
      type: descOk ? "success" : "error",
      msg: descOk
        ? `Fetched "${mapped.productTitle.slice(0, 60)}" with ${mapped.variations.length} variation(s). Set the prices before saving.`
        : "Product fetched, but the description could not be loaded.",
    });
  };

  /* gallery */
  const addGalleryImgs = () => {
    const urls = galleryInput
      .split(/[\n,]+/)
      .map(toCdn)
      .filter(Boolean)
      .filter((u, i, arr) => !gallery.includes(u) && arr.indexOf(u) === i);
    if (urls.length) setGallery((g) => [...g, ...urls]);
    setGalleryInput("");
  };
  const removeGalleryImg = (i) =>
    setGallery((g) => g.filter((_, idx) => idx !== i));
  const setAsMain = (url) => setField("productImg", toCdn(url));

  /* variations */
  const addVariation = () =>
    setVariations((v) => [
      ...v,
      { ...emptyVariation(), price: form.price || "" },
    ]);
  const removeVariation = (key) =>
    setVariations((v) => v.filter((x) => x.key !== key));
  const updateVariation = (key, name, value) => {
    setVariations((v) =>
      v.map((x) =>
        x.key === key
          ? {
              ...x,
              [name]: value,
              ...(name === "price" ? { priceTouched: true } : {}),
            }
          : x,
      ),
    );
    setErrors((e) => ({ ...e, [`var_${key}_${name}`]: undefined }));
  };

  const priceRange = useMemo(() => {
    const prices = variations
      .map((v) => Number(v.price))
      .filter((p) => !isNaN(p) && p > 0);
    if (!prices.length) return null;
    return [Math.min(...prices), Math.max(...prices)];
  }, [variations]);

  /* validation */
  const validate = () => {
    const e = {};
    if (!form.pid.trim()) e.pid = "Product ID is required";
    if (!form.productTitle.trim()) e.productTitle = "Title is required";
    if (!form.slug.trim()) e.slug = "Slug is required";
    if (!form.elevenCategoryId) e.elevenCategoryId = "Select a category";
    if (!form.productUrl.trim()) e.productUrl = "Product URL is required";
    if (!form.productImg.trim()) e.productImg = "Main image is required";
    if (
      form.price === "" ||
      isNaN(Number(form.price)) ||
      Number(form.price) < 0
    )
      e.price = "Enter a valid price";

    const seen = new Set();
    variations.forEach((v) => {
      if (!v.sku.trim()) e[`var_${v.key}_sku`] = "Required";
      else if (seen.has(v.sku.trim())) e[`var_${v.key}_sku`] = "Duplicate SKU";
      seen.add(v.sku.trim());
      if (!v.variationName.trim()) e[`var_${v.key}_variationName`] = "Required";
      if (!v.variationImg.trim()) e[`var_${v.key}_variationImg`] = "Required";
      if (v.price === "" || isNaN(Number(v.price)) || Number(v.price) < 0)
        e[`var_${v.key}_price`] = "Invalid";
    });

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const resetForm = () => {
    setForm(initialForm);
    setSlugTouched(false);
    setGallery([]);
    setGalleryInput("");
    setVariations([]);
    setErrors({});
    setDescTab("edit");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setAlert(null);
    if (!validate()) {
      setAlert({ type: "error", msg: "Please fix the highlighted fields." });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const payload = {
      ...form,
      pid: form.pid.trim(),
      productTitle: form.productTitle.trim(),
      slug: slugify(form.slug),
      elevenCategoryId: Number(form.elevenCategoryId),
      price: Number(form.price),
      productImg: toCdn(form.productImg),
      descHtml: normalizeDescHtml(form.descHtml),
      galleryImgs: [...new Set(gallery.map(toCdn).filter(Boolean))],
      variations: variations.map(({ key, priceTouched, ...v }) => ({
        ...v,
        variationImg: toCdn(v.variationImg),
        sku: v.sku.trim(),
        variationName: v.variationName.trim(),
        price: Number(v.price),
      })),
    };

    setSubmitting(true);
    try {
      const { data } = await axiosInstance.post(
        ENDPOINTS.createProduct,
        payload,
      );
      setAlert({
        type: "success",
        msg: data.msg || "Product created successfully",
      });
      resetForm();
    } catch (err) {
      setAlert({
        type: "error",
        msg: err.response?.data?.msg || "Failed to create product",
      });
    } finally {
      setSubmitting(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const selectedCategory = categories.find(
    (c) => String(c.id) === String(form.elevenCategoryId),
  );

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] content-start">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Add Product</h1>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setFetchOpen(true)}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-indigo-600 text-indigo-600 bg-white hover:bg-indigo-50 disabled:opacity-50"
          >
            Fetch Product
          </button>
          <button
            type="button"
            onClick={resetForm}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Reset
          </button>
          <button
            type="submit"
            form="add-product-form"
            disabled={submitting}
            className="px-5 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60 inline-flex items-center gap-2"
          >
            {submitting && (
              <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            )}
            {submitting ? "Saving..." : "Save Product"}
          </button>
        </div>
      </div>

      {alert && (
        <div
          className={`mb-4 flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${
            alert.type === "success"
              ? "bg-green-50 border-green-200 text-green-700"
              : "bg-red-50 border-red-200 text-red-700"
          }`}
        >
          <span>{alert.msg}</span>
          <button
            type="button"
            onClick={() => setAlert(null)}
            className="opacity-60 hover:opacity-100"
          >
            ✕
          </button>
        </div>
      )}

      <form id="add-product-form" onSubmit={onSubmit} noValidate>
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          {/* ---------- Left column ---------- */}
          <div className="xl:col-span-2 space-y-4">
            <Card
              title="Basic Information"
              subtitle="Core product details shown to customers"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="productTitle" required>
                    Product Title
                  </Label>
                  <input
                    id="productTitle"
                    name="productTitle"
                    value={form.productTitle}
                    onChange={onChange}
                    placeholder="e.g. Wireless Bluetooth Earbuds"
                    className={inputCls(errors.productTitle)}
                  />
                  <FieldError msg={errors.productTitle} />
                </div>

                <div>
                  <Label htmlFor="slug" required>
                    Slug
                  </Label>
                  <input
                    id="slug"
                    name="slug"
                    value={form.slug}
                    onChange={(e) => {
                      setSlugTouched(true);
                      setField("slug", slugify(e.target.value));
                    }}
                    placeholder="wireless-bluetooth-earbuds"
                    className={inputCls(errors.slug)}
                  />
                  <FieldError msg={errors.slug} />
                </div>

                <div>
                  <Label htmlFor="pid" required>
                    Product ID (PID)
                  </Label>
                  <input
                    id="pid"
                    name="pid"
                    value={form.pid}
                    onChange={onChange}
                    placeholder="Source product ID"
                    className={inputCls(errors.pid)}
                  />
                  <FieldError msg={errors.pid} />
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="productUrl" required>
                    Source Product URL
                  </Label>
                  <input
                    id="productUrl"
                    name="productUrl"
                    value={form.productUrl}
                    onChange={onChange}
                    placeholder="https://detail.1688.com/offer/..."
                    className={inputCls(errors.productUrl)}
                  />
                  <FieldError msg={errors.productUrl} />
                </div>
              </div>
            </Card>

            <Card title="Media" subtitle="Main image and gallery (image URLs)">
              <div className="flex flex-col sm:flex-row gap-5">
                <ImgThumb
                  src={form.productImg}
                  className="w-32 h-32 shrink-0"
                />
                <div className="flex-1">
                  <Label htmlFor="productImg" required>
                    Main Image URL
                  </Label>
                  <input
                    id="productImg"
                    name="productImg"
                    value={form.productImg}
                    onChange={onChange}
                    placeholder="https://..."
                    className={inputCls(errors.productImg)}
                  />
                  <FieldError msg={errors.productImg} />

                  <div className="mt-4">
                    <Label htmlFor="galleryInput">Gallery Images</Label>
                    <div className="flex gap-2">
                      <textarea
                        id="galleryInput"
                        rows={2}
                        value={galleryInput}
                        onChange={(e) => setGalleryInput(e.target.value)}
                        placeholder="Paste one or more URLs (comma or new line separated)"
                        className={inputCls(false) + " resize-none"}
                      />
                      <button
                        type="button"
                        onClick={addGalleryImgs}
                        className="px-4 text-sm font-medium rounded-lg border border-indigo-600 text-indigo-600 hover:bg-indigo-50"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {gallery.length > 0 && (
                <div className="mt-5 grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 gap-3">
                  {gallery.map((url, i) => (
                    <div key={url} className="relative group">
                      <ImgThumb src={url} className="w-full aspect-square" />
                      {toCdn(form.productImg) === url && (
                        <span className="absolute top-1 left-1 text-[10px] bg-indigo-600 text-white px-1.5 py-0.5 rounded">
                          Main
                        </span>
                      )}
                      <div className="absolute inset-0 bg-black/50 rounded-lg opacity-0 group-hover:opacity-100 transition flex flex-col items-center justify-center gap-1">
                        <button
                          type="button"
                          onClick={() => setAsMain(url)}
                          className="text-[11px] text-white bg-white/20 hover:bg-white/30 px-2 py-0.5 rounded"
                        >
                          Set main
                        </button>
                        <button
                          type="button"
                          onClick={() => removeGalleryImg(i)}
                          className="text-[11px] text-white bg-red-500/80 hover:bg-red-500 px-2 py-0.5 rounded"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card
              title="Variations"
              subtitle={
                variations.length
                  ? `${variations.length} variation(s)${
                      priceRange
                        ? ` · ৳${priceRange[0]} – ৳${priceRange[1]}`
                        : ""
                    }`
                  : "Optional — add sizes, colors, etc."
              }
              action={
                <button
                  type="button"
                  onClick={addVariation}
                  className="px-3 py-1.5 text-sm font-medium rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100"
                >
                  + Add Variation
                </button>
              }
            >
              {variations.length === 0 ? (
                <div className="text-center py-8 text-sm text-gray-400 border border-dashed border-gray-300 rounded-lg">
                  No variations added
                </div>
              ) : (
                <div className="space-y-3">
                  {variations.map((v, idx) => (
                    <div
                      key={v.key}
                      className="flex flex-col md:flex-row gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50/60"
                    >
                      <ImgThumb
                        src={v.variationImg}
                        className="w-16 h-16 shrink-0"
                      />
                      <div className="flex-1 grid grid-cols-2 md:grid-cols-12 gap-3">
                        <div className="col-span-1 md:col-span-3">
                          <Label>SKU</Label>
                          <input
                            value={v.sku}
                            onChange={(e) =>
                              updateVariation(v.key, "sku", e.target.value)
                            }
                            placeholder={`SKU-${idx + 1}`}
                            className={inputCls(errors[`var_${v.key}_sku`])}
                          />
                          <FieldError msg={errors[`var_${v.key}_sku`]} />
                        </div>
                        <div className="col-span-1 md:col-span-3">
                          <Label>Name</Label>
                          <input
                            value={v.variationName}
                            onChange={(e) =>
                              updateVariation(
                                v.key,
                                "variationName",
                                e.target.value,
                              )
                            }
                            placeholder="Black / XL"
                            className={inputCls(
                              errors[`var_${v.key}_variationName`],
                            )}
                          />
                          <FieldError
                            msg={errors[`var_${v.key}_variationName`]}
                          />
                        </div>
                        <div className="col-span-2 md:col-span-4">
                          <Label>Image URL</Label>
                          <input
                            value={v.variationImg}
                            onChange={(e) =>
                              updateVariation(
                                v.key,
                                "variationImg",
                                e.target.value,
                              )
                            }
                            placeholder="https://..."
                            className={inputCls(
                              errors[`var_${v.key}_variationImg`],
                            )}
                          />
                          <FieldError
                            msg={errors[`var_${v.key}_variationImg`]}
                          />
                        </div>
                        <div className="col-span-2 md:col-span-2">
                          <Label>Price (৳)</Label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={v.price}
                            onChange={(e) =>
                              updateVariation(v.key, "price", e.target.value)
                            }
                            className={inputCls(errors[`var_${v.key}_price`])}
                          />
                          <FieldError msg={errors[`var_${v.key}_price`]} />
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeVariation(v.key)}
                        title="Remove variation"
                        className="self-end md:self-center w-8 h-8 shrink-0 rounded-lg text-red-500 hover:bg-red-50"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card
              title="Description"
              subtitle="HTML is supported"
              action={
                <div className="inline-flex rounded-lg border border-gray-200 p-0.5 text-xs">
                  {["edit", "preview"].map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setDescTab(t)}
                      className={`px-3 py-1 rounded-md capitalize ${
                        descTab === t
                          ? "bg-indigo-600 text-white"
                          : "text-gray-600"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              }
            >
              {descTab === "edit" ? (
                <textarea
                  name="descHtml"
                  rows={10}
                  value={form.descHtml}
                  onChange={onChange}
                  placeholder="<p>Product description...</p>"
                  className={inputCls(false) + " font-mono text-xs"}
                />
              ) : (
                <div
                  className="prose prose-sm max-w-none min-h-[200px] border border-gray-200 rounded-lg p-4 overflow-auto"
                  dangerouslySetInnerHTML={{
                    __html:
                      normalizeDescHtml(form.descHtml) ||
                      '<p style="color:#9ca3af">Nothing to preview</p>',
                  }}
                />
              )}
            </Card>
          </div>

          {/* ---------- Right column ---------- */}
          <div className="space-y-4">
            <Card title="Organization">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="elevenCategoryId" required>
                    Category
                  </Label>
                  <select
                    id="elevenCategoryId"
                    name="elevenCategoryId"
                    value={form.elevenCategoryId}
                    onChange={onChange}
                    disabled={loadingCats}
                    className={inputCls(errors.elevenCategoryId)}
                  >
                    <option value="">
                      {loadingCats ? "Loading..." : "Select category"}
                    </option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <FieldError msg={errors.elevenCategoryId} />
                </div>

                <div>
                  <Label htmlFor="store">Source Store</Label>
                  <select
                    id="store"
                    name="store"
                    value={form.store}
                    onChange={onChange}
                    className={inputCls(false)}
                  >
                    {STORES.map((s) => (
                      <option key={s} value={s}>
                        {s === "A1688" ? "1688" : s}
                      </option>
                    ))}
                  </select>
                </div>

                <label className="flex items-center justify-between cursor-pointer pt-1">
                  <div>
                    <p className="text-sm font-medium text-gray-700">
                      Featured Product
                    </p>
                    <p className="text-xs text-gray-500">
                      Show on homepage sections
                    </p>
                  </div>
                  <span className="relative inline-flex">
                    <input
                      type="checkbox"
                      name="isFeatured"
                      checked={form.isFeatured}
                      onChange={onChange}
                      className="sr-only peer"
                    />
                    <span className="w-10 h-6 bg-gray-300 rounded-full peer-checked:bg-indigo-600 transition" />
                    <span className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow transition peer-checked:translate-x-4" />
                  </span>
                </label>
              </div>
            </Card>

            <Card title="Pricing">
              <Label htmlFor="price" required>
                Base Price (৳ BDT)
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                  ৳
                </span>
                <input
                  id="price"
                  name="price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.price}
                  onChange={onBasePriceChange}
                  placeholder="0.00"
                  className={inputCls(errors.price) + " pl-7"}
                />
              </div>
              <FieldError msg={errors.price} />
              {variations.length > 0 && (
                <div className="flex items-center justify-between gap-2 mt-2">
                  <p className="text-xs text-gray-500">
                    Auto-applied to variations you haven't edited.
                  </p>
                  {variations.some(
                    (v) => String(v.price) !== String(form.price),
                  ) && (
                    <button
                      type="button"
                      onClick={applyBasePriceToAll}
                      disabled={form.price === ""}
                      className="shrink-0 text-xs font-medium text-indigo-600 hover:underline disabled:opacity-40"
                    >
                      Apply to all
                    </button>
                  )}
                </div>
              )}
              {priceRange && (
                <p className="text-xs text-gray-500 mt-2">
                  Variation range: ৳{priceRange[0]} – ৳{priceRange[1]}
                </p>
              )}
            </Card>

            {/* Live preview card */}
            <Card title="Preview">
              <div className="rounded-lg border border-gray-200 overflow-hidden">
                <ImgThumb
                  src={form.productImg}
                  className="w-full aspect-square rounded-none border-0"
                />
                <div className="p-3">
                  {selectedCategory && (
                    <p className="text-[11px] uppercase tracking-wide text-gray-400">
                      {selectedCategory.name}
                    </p>
                  )}
                  <p className="text-sm font-medium text-gray-800 line-clamp-2 mt-0.5">
                    {form.productTitle || "Product title"}
                  </p>
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-indigo-600 font-bold">
                      ৳{form.price ? Number(form.price).toLocaleString() : "0"}
                    </p>
                    {form.isFeatured && (
                      <span className="text-[10px] font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                        Featured
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </form>

      <FetchProductModal
        open={fetchOpen}
        onClose={() => setFetchOpen(false)}
        onFetched={applyFetchedProduct}
      />
    </div>
  );
};

export default AddProduct;
